import { useEffect, useMemo, useState, createContext } from 'react';
import {
  getDisplayMode,
  getPlatform,
  checkIfInKioskMode,
  checkCanActivateWakeLock,
  checkCanOpenNewWindow,
  isWindowsSecureBrowser
} from '../scripts/KioskModeChecker';
import { CHROME, MACOS, WINDOWS } from '../scripts/Platform';
import { BROWSER, FULLSCREEN } from '../scripts/DisplayMode';
import { useTranslation } from 'react-i18next';
import { checkForSecureWrapper } from '../scripts/secure-wrapper/enforceSecureWrapper';

export const KioskContext = createContext();

export const PWA_CONFIG = {
  SECURE: 'secure',
  NON_SECURE: 'nonSecure'
};

const KioskProvider = ({ children }) => {
  const userAgent = navigator.userAgent;
  const platform = useMemo(() => getPlatform(), []);
  const displayMode = useMemo(() => getDisplayMode(), []);
  const [isSecure, setIsSecure] = useState(false);
  const [errorLog, setErrorLog] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [installedExtVersion, setInstalledExtVersion] = useState();
  const [canOpenNewWindow, setCanOpenNewWindow] = useState();
  const [canActivateWakeLock, setCanActivateWakeLock] = useState();
  const [takeATestDetails, setTakeATestDetails] =  useState();
  const { t } = useTranslation();

  useEffect(() => {
    if (
      displayMode.mode !== FULLSCREEN &&
      ![WINDOWS, MACOS].includes(platform)
    ) {
      setErrorLog(`Application is not in fullscreen display mode.`);
      setIsLoading(false);
      return;
    }

    if (
      displayMode.mode !== FULLSCREEN &&
      displayMode.mode !== BROWSER &&
      [WINDOWS, MACOS].includes(platform)
    ) {
      setErrorLog(`Application is not in fullscreen/browser display mode.`);
      setIsLoading(false);
      return;
    }

    if (![CHROME, WINDOWS, MACOS].includes(platform)) {
      setErrorLog(`${platform} - Platform not supported for secure testing.`);
      setIsLoading(false);
      return;
    }

    if (platform === CHROME) {
      checkIfInKioskMode(function (error, checkLogs) {
        if (error) {
          setInstalledExtVersion(error.installedExtensionVersion);
          setErrorLog(error.errorMessage);
        } else {
          setIsSecure(true);
          setInstalledExtVersion(checkLogs.installedExtensionVersion);
          setErrorLog(checkLogs.errorMessage);
        }
        setIsLoading(false);
      });
    } else if (
      platform === WINDOWS &&
      userAgent.indexOf('Edg/') > -1 &&
      displayMode.mode === FULLSCREEN
    ) {
      checkCanActivateWakeLock(function (data) {
        setCanActivateWakeLock(data.canActivateWakeLock);
        const response = checkCanOpenNewWindow();
        setCanOpenNewWindow(response.canOpenNewWindow);
        setErrorLog(`${data.errorMessage}${response.errorMessage}`);
        if (!response.canOpenNewWindow) {
          setIsSecure(true);
        }
        setIsLoading(false);
      });
    } else if (platform === WINDOWS && displayMode.mode === BROWSER) {
      let details;
      isWindowsSecureBrowser((info) => {
        try {
          details = JSON.parse(info);
        } catch (error) {
          details = { isNotSecureBrowser: true };
        }
        setTakeATestDetails(details);
        if (details?.isNotSecureBrowser !== true && details.secure === false) {
          setErrorLog(
            `Secure browser in use, but not in secure mode.${
              details.view ? ` Details - ${details.view}` : ''
            }.`
          );
          setIsSecure(false);
        } else if (details?.isNotSecureBrowser !== true && details.secure === true) {
          setIsSecure(true);
        } else {
          setIsSecure(false);
        }
        setIsLoading(false);
      });
    } else if (checkForSecureWrapper(platform, displayMode.mode)) {
      //MacOS/iOS secure wrapper - the check moved to enforceSecureWrapper so the
      //SecureWrapper component and the exit path can share it.
      setIsSecure(true);
      setIsLoading(false);
    } else {
      setErrorLog('Platform not supported!');
      setIsLoading(false);
    }
  }, [displayMode, platform, userAgent]);

  useEffect(() => {
    if(takeATestDetails && !takeATestDetails.isNotSecureBrowser && !takeATestDetails.secure && !isSecure) {
      window.alert(t('context.kioskProvider.pleaseCloseTakeATestWindow'))
      window.SecureBrowser?.security?.close(false);
    }
  }, [takeATestDetails, isSecure, t])

  return (
    <KioskContext.Provider
      value={{
        isSecure: isSecure,
        platform: platform,
        displayMode: displayMode.mode,
        isLoading: isLoading,
        errorLog: errorLog,
        installedExtVersion: installedExtVersion,
        canActivateWakeLock: canActivateWakeLock,
        canOpenNewWindow: canOpenNewWindow,
        takeATestDetails: takeATestDetails,
        setIsLoading: setIsLoading,
      }}
    >
      {children}
    </KioskContext.Provider>
  );
};

export default KioskProvider;
